import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryKeys } from '../queryConfig';
import { userVerificationApi, AuditLogEntry } from '../../services/userVerificationApi';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Hook to get the current user's verification status and requirements
 * @param companyId - Optional: if provided, only fetch this company's verifications + global
 */
export const useMyVerifications = (companyId?: string) => {
  return useQuery({
    queryKey: queryKeys.userVerifications.me(companyId),
    queryFn: async () => {
      const response = await userVerificationApi.getMyVerifications(companyId);
      if (!response.success) {
        throw new Error('Failed to fetch verifications');
      }
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to get pending verifications for admin review
 */
export const usePendingVerifications = (filters: {
  type?: 'company';
  page?: number;
  limit?: number;
} = {}) => {
  const { user } = useAuth();
  return useQuery({
    // Scope by reviewer id so safety_staff users switching accounts in the
    // same browser don't read the previous reviewer's portfolio-scoped cache.
    queryKey: queryKeys.userVerifications.pendingList({ ...filters, reviewerId: user?._id || '' }),
    queryFn: async () => {
      const response = await userVerificationApi.getPendingVerifications(filters);
      if (!response.success) {
        throw new Error('Failed to fetch pending verifications');
      }
      return {
        data: response.data,
        pagination: response.pagination,
      };
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to get users with pending verifications
 */
export const usePendingUsers = (filters: {
  page?: number;
  limit?: number;
} = {}) => {
  const { user } = useAuth();
  return useQuery({
    // Scope by reviewer id (see usePendingVerifications) to prevent cross-user
    // cache bleed when the same browser is used by multiple safety_staff.
    queryKey: queryKeys.userVerifications.pendingUsersList({ ...filters, reviewerId: user?._id || '' }),
    queryFn: async () => {
      const response = await userVerificationApi.getPendingUsers(filters);
      if (!response.success) {
        throw new Error('Failed to fetch pending users');
      }
      return {
        data: response.data,
        pagination: response.pagination,
      };
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to get a specific user's verification status (admin)
 * @param companyId - Optional: if provided, stats scoped to this company + global only
 */
export interface AuditLogFilters {
  action?: string;
  verificationType?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

interface AuditLogUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  cedula?: string;
}

/**
 * Hook to get a user's verification audit log with pagination and filters
 */
export const useUserVerificationAuditLog = (userId: string, filters: AuditLogFilters = {}) => {
  const queryParams: Record<string, string | number> = {
    page: filters.page || 1,
    limit: filters.limit || 20,
  };
  if (filters.action) queryParams.action = filters.action;
  if (filters.verificationType) queryParams.verificationType = filters.verificationType;
  if (filters.companyId) queryParams.companyId = filters.companyId;

  return useQuery<{
    user: AuditLogUser;
    entries: AuditLogEntry[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>({
    queryKey: queryKeys.userVerifications.auditLog(userId, queryParams),
    queryFn: async () => {
      const response = await userVerificationApi.getUserAuditLog(userId, queryParams);
      if (!response.success) {
        throw new Error('No se pudo cargar el historial de verificaciones.');
      }
      return {
        user: response.data!.user,
        entries: response.data!.entries,
        pagination: {
          page: response.pagination?.page ?? 1,
          limit: response.pagination?.limit ?? (queryParams.limit as number),
          total: response.pagination?.total ?? 0,
          pages: response.pagination?.pages ?? 0,
        },
      };
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useUserVerificationStatus = (userId: string, enabled = true, companyId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.userVerifications.user(userId), companyId].filter(Boolean),
    queryFn: async () => {
      const response = await userVerificationApi.getUserVerifications(userId, companyId);
      if (!response.success) {
        throw new Error('Failed to fetch user verifications');
      }
      return response.data;
    },
    enabled: !!userId && enabled,
    staleTime: 30 * 1000,
  });
};
